import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { UserAvatarComponent } from "../../../../shared/components/user-avatar/user-avatar.component";
import { AvatarPickerComponent } from "../../../../shared/components/avatar-picker/avatar-picker.component";

@Component({
  selector: "app-profile-modal",
  standalone: true,
  imports: [FormsModule, UserAvatarComponent, AvatarPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./profile-modal.component.html",
  styleUrl: "./profile-modal.component.scss",
})
export class ProfileModalComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);

  readonly displayName = input<string>("");
  readonly avatarUrl = input<string | null>(null);

  readonly save = output<{ name: string; avatarUrl: string | null }>();
  readonly close = output<void>();

  readonly editingName = signal<string>("");
  readonly editingAvatarUrl = signal<string | null>(null);
  readonly pickerOpen = signal(false);

  readonly isUploadedPhoto = computed(
    () => this.editingAvatarUrl()?.startsWith("data:") ?? false,
  );

  ngOnInit(): void {
    this.editingName.set(this.displayName());
    this.editingAvatarUrl.set(this.avatarUrl());
  }

  handleEditAvatar(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 256;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas
          .getContext("2d")!
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        this.editingAvatarUrl.set(canvas.toDataURL("image/jpeg", 0.75));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeEditAvatar(): void {
    this.editingAvatarUrl.set(null);
  }

  onPickerSelect(url: string): void {
    this.editingAvatarUrl.set(url);
    this.pickerOpen.set(false);
  }

  onSave(): void {
    const name = this.editingName().trim();
    if (name.length < 2) return;
    this.save.emit({ name, avatarUrl: this.editingAvatarUrl() });
  }
}
